import * as React from "react";
import { NavMenuOption, PageLayout } from "components/PageLayout/PageLayout";
import { BreadcrumbItem } from "components/Breadcrumbs/Breadcrumbs";
import { Typography, Box, Button } from "@mui/material";
import { ClientContactsReportsList } from "./ClientContactsReportsList";
import { useMutation, useQueryClient } from "react-query";
import { MetricsService } from "gen/clients";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";
import { ConfirmationDialog } from "components/ConfirmationDialog/ConfirmationDialog";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import AddIcon from "@mui/icons-material/Add";

const breadcrumbs: BreadcrumbItem[] = [
  {
    path: "/analytics",
    label: "Analytics"
  },
  {
    path: "/analytics/clientContactsReports",
    label: "Client Contacts Report",
    active: true
  }
];

const ClientContactsReportsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [confirmationDialogOpen, setConfirmationDialogOpen] = React.useState(false);
  const [hasRunningReports, setHasRunningReports] = React.useState(false);

  const {
    mutate: createReport,
    isLoading: isCreating,
    error: createError
  } = useMutation(MetricsService.createClientContactsReport, {
    onSuccess: () => {
      queryClient.invalidateQueries(["listClientContactsReports"]);
      setConfirmationDialogOpen(false);
    }
  });

  const handleCreateReportClick = React.useCallback(() => {
    setConfirmationDialogOpen(true);
  }, []);

  const handleConfirmCreateReport = React.useCallback(() => {
    createReport();
  }, [createReport]);

  const handleCloseConfirmationDialog = React.useCallback(() => {
    setConfirmationDialogOpen(false);
  }, []);

  const handleRunningReportsChange = React.useCallback((hasRunning: boolean) => {
    setHasRunningReports(hasRunning);
  }, []);

  return (
    <PageLayout
      selectedNavMenu={NavMenuOption.ANALYTICS}
      title={<Typography variant="h6">Client Contacts Report</Typography>}
      breadcrumbs={breadcrumbs}
    >
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="body1" color="text.secondary">
          Generate and view reports containing detailed information about client contacts, including their status, last
          visit, and Cognito information.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateReportClick}
          disabled={isCreating || hasRunningReports}
        >
          {isCreating ? "Creating..." : hasRunningReports ? "Report Generation in Progress..." : "Generate New Report"}
        </Button>
      </Box>

      <ClientContactsReportsList onRunningReportsChange={handleRunningReportsChange} />
      {createError && <SnackbarApiError error={createError} />}

      {/* Generate Report Confirmation Dialog */}
      {confirmationDialogOpen && (
        <ConfirmationDialog
          title="Generate Client Contacts Report"
          content={
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Warning:</strong> Client Contacts report generation is a time-consuming process that consumes
                significant system resources.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The report may take several minutes to complete depending on the number of client contacts in the
                system. Are you sure you want to proceed with generating a new report?
              </Typography>
            </>
          }
          onClose={handleCloseConfirmationDialog}
          onConfirm={handleConfirmCreateReport}
          confirmationButtonColor="primary"
          isLoading={isCreating}
          error={createError ? <ApiErrorMessage apiError={createError} /> : undefined}
        />
      )}
    </PageLayout>
  );
};

export { ClientContactsReportsPage };
